import React, {forwardRef, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import RichTextEditor from '@ey/canvascoreservices/RichTextEditor';
import {labels} from '../../../util/uiconstants';
import messages from './LoadMessages';
import {useSelector} from 'react-redux';
import Utility from '../../../util/utils'; //'../../../util/utilityfunctions';
import Quill from 'quill';
import RichTextHeader from './RichTextHeader';
import MotifRichTextEditor from '@ey/motif-react-wrapper/RichTextEditor';

const RichTextEditorWrapper = forwardRef((props, ref) => {
	const {
		allowComments,
		addClearedCommentClass,
		addOpenCommentClass,
		cancelAddComment,
		charCountChanged,
		commentTooltip,
		commentId,
		commentIdToRemove,
		charactersLabel,
		dataAutoID,
		disabled,
		label,
		maxCharCount,
		numberFormat,
		ofLabel,
		onChange,
		onEsc,
		openAddCommentBox,
		onCtrlSave,
		optional,
		optionalText,
		placeholder,
		required,
		removeComment,
		showCharacterCount,
		showMaxCharacterCount,
		setRef,
		setEditor,
		useMinimalToolset,
		value,
		useGuidedWorkFlowToolset,
		theme,
		resize,
		useMotifEditor = true,
		error,
		getPlainText
	} = props;

	const richTextToPlainText = (richText) => {
		let divElement = document.createElement('div');
		divElement.innerHTML = richText ?? '';
		return divElement.innerText.replace(/\uFEFF/g, '').trim();
	};

	const removeEmptyParagraph = useCallback((text) => {
		if (text === '<p><br></p>') {
			return '';
		} else {
			const parser = new DOMParser();
			const doc = parser.parseFromString(text, 'text/html');
			const innerText =
				doc.body.textContent?.trim() || doc.body.innerText?.trim();
			if (innerText === '') {
				return '';
			}
		}
		return text;
	}, []);

	const [charCount, setCharCount] = useState(value ? value.length : 0);
	const [htmlValue, setHtmlValue] = useState(value);

	const onCharCountChanged = (count) => {
		setCharCount(count);
		charCountChanged && charCountChanged(count);
	};

	const user = useSelector((store) => store.userPreferences);

	useEffect(() => {
		const plainText = richTextToPlainText(value);
		getPlainText?.(plainText);
		setEditor?.(ref);
	}, []);

	const editorModules = {
		toolbar: {
			container: '#toolbar'
		}
	};

	const sanitizeHtml = (html) => {
		return html?.replace(/style="([^"]*?)\s+"/g, 'style="$1"');
	};

	const handleChange = (html, delta, source) => {
		const trimmed = removeEmptyParagraph(html);
		const plainText = richTextToPlainText(trimmed);

		onChange?.(trimmed, delta, source);
		getPlainText?.(plainText);

		setHtmlValue(trimmed);
		onCharCountChanged(trimmed.length);
	};

	const alignStyle = Quill.import('attributors/style/align');
	Quill.register(alignStyle, true);

	const Size = Quill.import('attributors/style/size');
	Size.whitelist = [
		'8px',
		'9px',
		'10px',
		'11px',
		'12px',
		'14px',
		'16px',
		'18px',
		'20px',
		'22px',
		'24px',
		'26px',
		'28px',
		'36px',
		'48px',
		'72px'
	];
	Quill.register(Size, true);

	const message = messages;
	return (
		<StyledRichTextEditor className="StyledRichTextEditor ">
			{useMotifEditor === true ? (
				<>
					<RichTextHeader {...props} charCount={charCount} />
					<section className={`${error ? 'motif-input-invalid' : ''}`}>
						<div id="toolbar">
							<button className="ql-bold" title={messages.bold} />
							<button className="ql-italic" title={messages.italic} />
							<button className="ql-underline" title={messages.underline} />
							<select className="ql-color" title={messages.foreColor}>
								<option value="#FF4136" />
								<option value="#189D3E" />
								<option value="#FFE600" />
								<option value="#FF6D00" />
								<option value="#0070C0" />
							</select>
							<select className="ql-background" title={messages.backColor}>
								<option value="#FF4136" />
								<option value="#189D3E" />
								<option value="#FFE600" />
								<option value="#FF6D00" />
								<option value="#0070C0" />
							</select>
							<select className="ql-size" title={messages.fontSize}>
								<option
									value={messages.fontSizeOptions[0].value}
									title={messages.fontSizeOptions[0].text}
								>
									{messages.fontSizeOptions[0].text}
								</option>
								<option
									value={messages.fontSizeOptions[1].value}
									title={messages.fontSizeOptions[1].text}
								>
									{messages.fontSizeOptions[1].text}
								</option>
								<option
									value={messages.fontSizeOptions[2].value}
									title={messages.fontSizeOptions[2].text}
								>
									{messages.fontSizeOptions[2].text}
								</option>
								<option
									value={messages.fontSizeOptions[3].value}
									title={messages.fontSizeOptions[3].text}
								>
									{messages.fontSizeOptions[3].text}
								</option>
								<option
									value={messages.fontSizeOptions[4].value}
									title={messages.fontSizeOptions[4].text}
									selected
								>
									{messages.fontSizeOptions[4].text}
								</option>
								<option
									value={messages.fontSizeOptions[5].value}
									title={messages.fontSizeOptions[5].text}
								>
									{messages.fontSizeOptions[5].text}
								</option>
								<option
									value={messages.fontSizeOptions[6].value}
									title={messages.fontSizeOptions[6].text}
								>
									{messages.fontSizeOptions[6].text}
								</option>
								<option
									value={messages.fontSizeOptions[7].value}
									title={messages.fontSizeOptions[7].text}
								>
									{messages.fontSizeOptions[7].text}
								</option>
								<option
									value={messages.fontSizeOptions[8].value}
									title={messages.fontSizeOptions[8].text}
								>
									{messages.fontSizeOptions[8].text}
								</option>
								<option
									value={messages.fontSizeOptions[9].value}
									title={messages.fontSizeOptions[9].text}
								>
									{messages.fontSizeOptions[9].text}
								</option>
								<option
									value={messages.fontSizeOptions[10].value}
									title={messages.fontSizeOptions[10].text}
								>
									{messages.fontSizeOptions[10].text}
								</option>
								<option
									value={messages.fontSizeOptions[11].value}
									title={messages.fontSizeOptions[11].text}
								>
									{messages.fontSizeOptions[11].text}
								</option>
								<option
									value={messages.fontSizeOptions[12].value}
									title={messages.fontSizeOptions[12].text}
								>
									{messages.fontSizeOptions[12].text}
								</option>
								<option
									value={messages.fontSizeOptions[13].value}
									title={messages.fontSizeOptions[13].text}
								>
									{messages.fontSizeOptions[13].text}
								</option>
								<option
									value={messages.fontSizeOptions[14].value}
									title={messages.fontSizeOptions[14].text}
								>
									{messages.fontSizeOptions[14].text}
								</option>
								<option
									value={messages.fontSizeOptions[15].value}
									title={messages.fontSizeOptions[15].text}
								>
									{messages.fontSizeOptions[15].text}
								</option>
							</select>
							<button
								className="ql-align"
								value="justify"
								title={messages.alignJustify}
							/>
							<button
								className="ql-align"
								value="center"
								title={messages.alignCenter}
							/>
							<button
								className="ql-align"
								value="right"
								title={messages.alignRight}
							/>
							<button
								className="ql-list"
								value={'ordered'}
								title={messages.orderedList}
							/>
							<button
								className="ql-list"
								value={'bullet'}
								title={messages.bulletList}
							/>
							<button className="ql-clean" title={messages.cleanFormatting} />
						</div>
						<MotifRichTextEditor
							value={htmlValue}
							onChange={handleChange}
							placeholder={placeholder}
							modules={editorModules}
							className="text-editor customScrollbar"
						/>
					</section>
				</>
			) : (
				<RichTextEditor
					useMotifEditor={false}
					allowComments={allowComments}
					addClearedCommentClass={addClearedCommentClass}
					addOpenCommentClass={addOpenCommentClass}
					cancelAddComment={cancelAddComment}
					charCountChanged={charCountChanged}
					characterCountPrefix={charactersLabel || labels.characters}
					charactersLabel={charactersLabel}
					commentId={commentId}
					commentIdToRemove={commentIdToRemove}
					commentTitle={commentTooltip}
					dataAutoID={dataAutoID}
					disabled={disabled}
					isTranslate={false}
					label={label}
					maxCharCount={maxCharCount}
					messages={message}
					numberFormat={numberFormat}
					ofLabel={ofLabel}
					onChange={onChange}
					onEsc={onEsc}
					openAddCommentBox={openAddCommentBox}
					onCtrlSave={onCtrlSave || onChange}
					optional={optional}
					optionalText={optionalText}
					placeholder={placeholder || labels.placeholder}
					required={required}
					resizable={resize}
					removeComment={removeComment}
					showCharacterCount={showCharacterCount}
					showMaxCharacterCount={showMaxCharacterCount}
					setRef={setRef}
					userLanguage={user ? user.displayLanguage : 'en-US'}
					useMinimalToolset={useMinimalToolset}
					useGuidedWorkFlowToolset={useGuidedWorkFlowToolset}
					value={value ? sanitizeHtml(value) : ''}
					theme={theme || Utility.getTheme()}
				/>
			)}
		</StyledRichTextEditor>
	);
});
RichTextEditorWrapper.displayName = 'RichTextEditorWrapper';

export default RichTextEditorWrapper;

const StyledRichTextEditor = styled.section`
	.motif-input-invalid {
		border: var(--px-1) solid var(--input-error--border-color);
	}
	.ql-snow.ql-toolbar button,
	.ql-color-picker {
		height: var(--px-24);
		padding: var(--px-1) var(--px-2);
		width: var(--px-24);
		border-radius: var(--rich-text-editor-toolbar-icon--border-radius);
	}
	.ql-snow.ql-toolbar button,
	.ql-color-picker,
	.ql-picker-label {
		.ql-stroke,
		.ql-fill {
			stroke: var(--rich-text-editor-toolbar-icon--fill);
			fill: none;
		}

		&:hover,
		&:active,
		&:focus-visible {
			background-color: var(--rich-text-editor-toolbar-icon-hover--bg-color);
			color: var(--rich-text-editor-container--color) !important;
			.ql-stroke,
			.ql-fill {
				stroke: var(--rich-text-editor-container--color) !important;
			}
		}
	}
	.ql-container {
		font-size: var(--px-12);
		min-height: var(--px-140);
		border-bottom-left-radius: var(--border-radius-small);
		border-bottom-right-radius: var(--border-radius-small);
		border-top: 0 !important;
		.ql-editor {
			ol {
				li {
					.ql-ui {
						&::before {
							font-family: 'Helvetica', 'Arial', sans-serif;
						}
					}
				}
			}
			p {
				span {
					line-height: normal;
				}
			}
		}
	}
	.ql-toolbar {
		border-top-left-radius: var(--border-radius-small);
		border-top-right-radius: var(--border-radius-small);
		border-bottom: none;
		.ql-color-picker .ql-picker-options {
			width: var(--px-140);
		}
		.ql-size .ql-picker-options {
			height: var(--px-140);
			overflow-y: auto;
		}
	}
	.rich-text-kendo-editor {
		.kendo-editor-label {
			font-size: var(--px-14);
			font-weight: 400;
			line-height: var(--px-20);
			margin-bottom: var(--px-4);
			margin-right: var(--px-10);
		}
		.k-editor {
			background-color: var(--neutrals-00white);
			border: var(--px-1) solid var(--neutrals-200);
			.k-toolbar {
				padding: var(--px-18) var(--px-16);
				border: 0;
				border-bottom: var(--px-1) solid var(--neutrals-200);
				box-shadow: none;
				background-color: var(--neutrals-00white);
				.k-button-group {
					.k-dropdownlist {
						width: var(--px-154);
						background-color: var(--neutrals-00white);
						border: var(--px-1) solid var(--neutrals-200) !important;
						color: var(--neutrals-900);
						.k-input-inner {
							font-size: var(--px-14) !important;
							font-weight: 300 !important;
							line-height: var(--px-22) !important;
						}
						.k-button {
							border: 0 !important;
							border-left: var(--px-1) solid var(--neutrals-200) !important;
						}
					}
					.k-picker {
						background-color: var(--neutrals-00white);
						border: var(--px-1) solid var(--neutrals-200) !important;
						color: var(--neutrals-900);
						border-radius: var(--px-8) !important;
						.k-button {
							border: 0 !important;
						}
					}
					.k-button {
						background-color: var(--neutrals-00white);
						border: var(--px-1) solid var(--neutrals-200) !important;
						color: var(--neutrals-900);
						border-radius: var(--px-8) !important;
						& + .k-button {
							border-radius: 0 !important;
						}
						&.k-group-start {
							border-radius: var(--px-8) 0 0 var(--px-8) !important;
						}
						&.k-group-end {
							border-radius: 0 var(--px-8) var(--px-8) 0 !important;
						}
					}
				}
			}
			.k-editor-content {
				padding: var(--px-16);
			}
		}
	}
`;
